import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Col, Container, Row } from 'reactstrap';
import ReactLoading from 'react-loading';
import { get } from 'lodash-es';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Radio from '@mui/material/Radio';
import { imagePath, isProd } from '../../utilities/UrlPaths';

import ColorBlockSelect from './ColorBlockSelect';
import PreviewCatalog from './PreviewCatalog';

const PLSettings = () => {
  const [buttonColorData, setButtonColorData] = useState([]);
  const [fontColorData, setFontColorData] = useState([]);
  const [fontData, setFontData] = useState([]);
  const [languageData, setLanguageData] = useState([]);
  const [houseLogoForPreview, setHouseLogoForPreview] = useState('');
  const [houseLogo, setHouseLogo] = useState('');
  const [consoleLogo, setConsoleLogo] = useState('');
  const [houseLogoImageError, setHouseLogoImageError] = useState(false);
  const [consoleLogoImageError, setConsoleLogoImageError] = useState(false);
  const [houseLogoDisplay, setHouseLogoDisplay] = useState('');
  const [consoleLogoDisplay, setConsoleLogoDisplay] = useState('');
  const [buttonColorID, setButtonColorID] = useState(0);
  const [fontColorID, setFontColorID] = useState(0);
  const [fontStyleID, setFontStyleID] = useState(0);
  const [buttonColorHex, setButtonColorHex] = useState('');
  const [fontColorHex, setFontColorHex] = useState('');
  const [fontStyleName, setFontStyleName] = useState('');
  const [buttonColorName, setButtonColorName] = useState('');
  const [fontColorName, setFontColorName] = useState('');
  const [languageID, setLanguageID] = useState(0);
  const [languageLabel, setLanguageLabel] = useState('');
  const [languageLanguage, setLanguageLanguage] = useState('');
  const [showPastAuctions, setShowPastAuctions] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);
  const [consoleLogoUploadFlag, setConsoleLogoUploadFlag] = useState(false);
  const [houseLogoUploadFlag, setHouseLogoUploadFlag] = useState(false);
  const [postSuccessFlag, setPostSuccessFlag] = useState(false);
  const [showLogoMessage, setShowLogoMessage] = useState(false);
  const [originalHouseLogo, setOriginalHouseLogo] = useState('');
  const { houseRef } = useSelector(state => state.house);

  const getData = async () => {
    try {
      const [buttonResponse, fontColorResponse, fontResponse, languageResponse, settingsResponse] = await Promise.all([
        axios.get('/app/button-colors'),
        axios.get('/app/font-colors'),
        axios.get('/app/font-styles'),
        axios.get('/app/languages'),
        axios.get(`/app/house/${houseRef}/ib-settings`)
      ]);

      setButtonColorData(buttonResponse.data.data.buttonColors);
      setFontColorData(fontColorResponse.data.data.fontColors);
      setFontData(fontResponse.data.data.fontStyles);
      setLanguageData(languageResponse.data.data.languages);
      const { consoleLogoURL, houseLogoURL, ibSetting } = settingsResponse.data.data.house;
      setHouseLogoForPreview(houseLogoURL);
      setConsoleLogoDisplay(consoleLogoURL.split('/').pop());
      setHouseLogoDisplay(houseLogoURL.split('/').pop());
      setOriginalHouseLogo(houseLogoURL);
      setButtonColorID(ibSetting.colorStyleSettings.buttonColor.id);
      setButtonColorHex(ibSetting.colorStyleSettings.buttonColor.hex);
      setButtonColorName(ibSetting.colorStyleSettings.buttonColor.name);
      setFontColorID(ibSetting.colorStyleSettings.fontColor.id);
      setFontColorHex(ibSetting.colorStyleSettings.fontColor.hex);
      setFontColorName(ibSetting.colorStyleSettings.fontColor.label);
      setFontStyleID(ibSetting.colorStyleSettings.fontStyle.id);
      setFontStyleName(ibSetting.colorStyleSettings.fontStyle.label);
      setLanguageID(ibSetting.defaultLanguage.id);
      setLanguageLabel(ibSetting.defaultLanguage.label);
      setLanguageLanguage(ibSetting.defaultLanguage.language);
      setShowPastAuctions(ibSetting.displayPastAuctions);
      setDataLoading(false);
    } catch (err) {
      console.log('err', err);
    }
  };

  useEffect(() => {
    if (houseRef) getData();
  }, [houseRef]);

  const handleFontStyleChange = (id, label) => {
    setFontStyleID(id);
    setFontStyleName(label);
  };
  const handleFontColorChange = (id, hex, name) => {
    setFontColorID(id);
    setFontColorHex(hex);
    setFontColorName(name);
  };
  const handleButtonColorChange = (id, hex, name) => {
    setButtonColorID(id);
    setButtonColorHex(hex);
    setButtonColorName(name);
  };
  const handleLanguageChange = (id, label, language) => {
    setLanguageID(id);
    setLanguageLabel(label);
    setLanguageLanguage(language);
  };
  const handlePastAuctions = (e) => {
    setShowPastAuctions(e.target.value === 'true');
  };

  const handleConsoleLogoChange = (e) => {
    setConsoleLogoImageError(false);
    const logoFile = e.target.files[0];
    setConsoleLogo(logoFile);
    //  validate dimensions & type
    const fileType = logoFile.type;
    const img = new Image();
    img.src = URL.createObjectURL(logoFile);
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      if (width === 240 && height === 65 && fileType === 'image/jpeg') {
        setConsoleLogoUploadFlag(true);
        setConsoleLogoDisplay(logoFile.name);
      } else {
        setConsoleLogoImageError(true);
      }
    };
  };

  const handleHouseLogoChange = (e) => {
    setHouseLogoImageError(false);
    const logoFile = e.target.files[0];
    setHouseLogo(logoFile);
    //  validate dimensions & type
    const fileType = logoFile.type;
    const img = new Image();
    img.src = URL.createObjectURL(logoFile);
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      if (width === 350 && height === 100 && fileType === 'image/jpeg') {
        setHouseLogoUploadFlag(true);
        setHouseLogoDisplay(logoFile.name);
        setHouseLogoForPreview(URL.createObjectURL(logoFile));
      } else {
        setHouseLogoImageError(true);
      }
    };
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    const fieldsURL = `/app/house/${houseRef}/ib-settings`;
    const consoleLogoUploadURL = `/app/house/${houseRef}/console-logo`;
    const siteLogoUploadURL = `/app/house/${houseRef}/logo`;
    setPostSuccessFlag(false);

    const dataObject = {
      colorStyleSettings: {
        fontColor: {
          hex: fontColorHex,
          id: fontColorID,
          label: fontColorName,
        },
        fontStyle: {
          id: fontStyleID,
          label: fontStyleName,
        },
        buttonColor: {
          hex: buttonColorHex,
          id: buttonColorID,
          name: buttonColorName,
        },
      },
      defaultLanguage: {
        id: languageID,
        label: languageLabel,
        language: languageLanguage,
      },
      displayPastAuctions: showPastAuctions,
    };

    try {
      const postData = await axios.post(
        fieldsURL,
        dataObject,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );

      const config = {
        headers: {
          'content-type': 'multipart/form-data',
        },
      };

      let consoleLogoSuccess = true;
      if (consoleLogoUploadFlag) {
        const consoleFormData = new FormData();
        consoleFormData.append('file', consoleLogo);
        consoleFormData.append('fileName', consoleLogo.name);
        const consoleLogoResponse = await axios.post(consoleLogoUploadURL, consoleFormData, config);
        if (consoleLogoResponse.data.httpStatus !== 200) {
          consoleLogoSuccess = false;
        }
      }

      let houseLogoSuccess = true;
      if (houseLogoUploadFlag) {
        const houseFormData = new FormData();
        houseFormData.append('file', houseLogo);
        houseFormData.append('fileName', houseLogo.name);
        const houseLogoResponse = await axios.post(siteLogoUploadURL, houseFormData, config);
        if (houseLogoResponse.data.httpStatus === 200) {
          // setHouseLogoForPreview(URL.createObjectURL(houseLogo));
          setShowLogoMessage(true);
        } else {
          setHouseLogoForPreview(originalHouseLogo);
          houseLogoSuccess = false;
        }
      }

      if (get(postData, 'data.httpStatus', 0) === 200 && houseLogoSuccess && consoleLogoSuccess) {
        setPostSuccessFlag(true);
        setTimeout(() => {
          setPostSuccessFlag(false);
        }, 5000);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const azaPrefix = isProd ? 'aza' : 'stageaza';
  const azaServer = `https://${azaPrefix}.invaluableauctions.com`;

  const catalogObject = [
    {
      previewImageSrc: `${azaServer}/img/IbSettingsPreview/jewelry_bracelet.jpg`,
      catTitle: 'Modern Jewelry',
      catLocation: 'Boston, MA, US',
      catTimeLeft: '24 days 3 hours',
      catID: 1,
    },
    {
      previewImageSrc: `${azaServer}/img/IbSettingsPreview/art_painting1.jpg`,
      catTitle: 'Paintings & Prints',
      catLocation: 'New York, NY, US',
      catTimeLeft: '3 days 19 hours',
      catID: 2,
    },
    {
      previewImageSrc: `${azaServer}/img/IbSettingsPreview/art_painting3.jpg`,
      catTitle: 'August Fine Art Sale',
      catLocation: 'Chicago, IL, US',
      catTimeLeft: '1 days 2 hours',
      catID: 3,
    },
    {
      previewImageSrc: `${azaServer}/img/IbSettingsPreview/art_painting2.jpg`,
      catTitle: 'Decorative Arts',
      catLocation: 'London, UK',
      catTimeLeft: '10 days 9 hours',
      catID: 4,
    },
  ];

  if (dataLoading) {
    return <ReactLoading className="loading-spinner" type="spin" color="black" />;
  }

  return (
    <Container>
      <Row>
        <Col xs={12} className="my-5">
          <h1>Private Label Settings</h1>
          <div>
            Please select your preferences to set up your website.
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <div>
            <div>
              <h3>Logo</h3>
              <div>
                240 x 65px JPG format (this will appear in the live console)
              </div>
              <div>
                <input type="text" value={consoleLogoDisplay} id="consoleFileDisplay" className="mr-3" readOnly />
                <input type="file" defaultValue="" id="consoleFileField" hidden className="mr-3" onChange={(e) => handleConsoleLogoChange(e)} />
                <label htmlFor="consoleFileField" className="upload-button small">Upload Console Logo</label>
              </div>
              {consoleLogoImageError && (
              <div className="error-text small">
                Console logo must be a JPG with dimensions of 240px x 65px
              </div>
              )}
              <div className="mt-3">
                350 x 100px JPG format (this will appear on the top of your site)
              </div>
              <div>
                <input type="text" value={houseLogoDisplay} id="houseFileDisplay" className="mr-3" readOnly />
                <input type="file" defaultValue="" id="houseFileField" hidden className="mr-3" onChange={(e) => handleHouseLogoChange(e)} />
                <label htmlFor="houseFileField" className="upload-button small">Upload Site Logo</label>
              </div>
              {houseLogoImageError && (
                <div className="error-text small">
                  Site logo must be a JPG with dimensions of 350px x 100px
                </div>
              )}
            </div>

            <div className="mt-4">
              <h3>Font Styles</h3>
              <FormControl sx={{ m: 1, minWidth: 120 }}>
                <Select
                  labelId="fontStyleSelectLabel"
                  id="fontStyleSelect"
                  value={fontStyleID}
                  size="small"
                  className="mt-n2"
                >
                  {fontData && fontData.map(font => (
                    <MenuItem
                      value={font.id}
                      label={font.label}
                      key={`font_${font.id}`}
                      style={{ fontFamily: font.label }}
                      onClick={() => handleFontStyleChange(font.id, font.label)}
                    >
                      {font.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="mt-4">
              <h3>Font Color</h3>
              <FormControl sx={{ m: 1, minWidth: 100 }}>
                <Select
                  labelId="fontColorSelectLabel"
                  id="fontColorSelect"
                  value={fontColorID}
                  // label="Font Color"
                  size="small"
                  className="mt-n2"
                >
                  {fontColorData && fontColorData.map(font => (
                    <MenuItem
                      value={font.id}
                      hex={font.hex}
                      key={`font_${font.id}`}
                      onClick={() => handleFontColorChange(font.id, font.hex, font.label)}
                    >
                      <div style={{ backgroundColor: font.hex, height: '25px', width: '40px' }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="mt-4">
              <h3>Button Color</h3>
              <div className="mt-n2">
                <ColorBlockSelect
                  data={buttonColorData}
                  handleButtonColorChange={handleButtonColorChange}
                  buttonColorID={buttonColorID}
                  buttonColorHex={buttonColorHex}
                />
              </div>
            </div>

            <div className="mt-4">
              <h3>Language Preference</h3>
              <FormControl sx={{ m: 1, minWidth: 200 }}>
                <Select
                  labelId="LanguageSelectLabel"
                  id="LanguageSelect"
                  value={languageID}
                  size="small"
                  className="mt-n2"
                >
                  {languageData && languageData.map(lang => (
                    <MenuItem
                      value={lang.id}
                      key={`lang_${lang.id}`}
                      onClick={(e) => handleLanguageChange(lang.id, lang.label, lang.language)}
                    >
                      {lang.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="mt-4">
              <h3>Auction Results</h3>
              <div className="mt-n1">
                Do you want to display past auctions on Private Label?
              </div>
              <Radio
                checked={showPastAuctions === true}
                onChange={(e) => handlePastAuctions(e)}
                value={true}
                name="pastAuctionsRadioGroup"
              />
              <span className="mr-3">Yes</span>
              <Radio
                checked={showPastAuctions === false}
                onChange={(e) => handlePastAuctions(e)}
                value={false}
                name="pastAuctionsRadioGroup"
              />
              <span>No</span>
            </div>
            <div className="my-5">
              {postSuccessFlag ? (
                <div className="alert-success">
                  <div>
                    Your changes have been saved.
                    {showLogoMessage ? ' Logos may take several hours to appear.' : ''}

                  </div>
                </div>
              ) : (
                <>
                  <Button
                    variant="contained"
                    className="mui-primary-button"
                    onClick={(e) => handleSubmitForm(e)}
                  >
                    Save All
                  </Button>
                  <span className="ml-4">
                    <a href={`${imagePath}/my-auctions/current`}>Cancel</a>
                  </span>
                </>

              )}
            </div>

          </div>
        </Col>
        <Col xs={6}>
          <div>
            <h3>Page Preview</h3>
            <div className="border">
              <div className="border-bottom pb-2">
                <div className="float-left ml-2 mt-2">
                  <img src={houseLogoForPreview} height="50" width="175" alt="house logo" />
                </div>
                <div className="float-right mr-2 small mt-2">
                  Help &nbsp; Log In
                </div>
                <div className="clearfix" />
              </div>
              <div className="border-bottom font-weight-bold pb-3 my-3 mx-2">
                Upcoming Auctions &nbsp;  Past Auctions
              </div>
              {catalogObject && catalogObject.map(catalog => (
                <PreviewCatalog
                  catalogData={catalog}
                  buttonColor={buttonColorHex}
                  fontColor={fontColorHex}
                  fontStyle={fontStyleName}
                  className="ml-2"
                  key={catalog.catID}
                />
              ))}

              <div className="small ml-2 my-3">
                Powered by Invaluable
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default PLSettings;
